import type { CSSProperties } from "react";
import { EyeOff, KeyRound, Timer, UserX } from "lucide-react";
import privacyLock from "../../assets/landing/privacy-lock.webp";
import { cn } from "../../lib/cn";
import { Star } from "../decor";
import { Scene } from "./stage/Scene";
import { SplitChars } from "./stage/Split";
import * as fx from "./stage/fx";
import type { Timeline } from "./stage/choreography";

const CARDS = [
  {
    icon: KeyRound,
    title: "Unguessable links",
    body: "Every link carries a 192-bit random token, and only a hashed version is ever stored — links can't be guessed or reverse-engineered.",
  },
  {
    icon: UserX,
    title: "No account for caregivers",
    body: "Caregivers just open the link. Nothing to install, no password, no sign-up — and we don't collect their details.",
  },
  {
    icon: EyeOff,
    title: "Share only what you choose",
    body: "Each link is scoped to the exact sections you pick. Hidden sections aren't just hidden — they're never sent to the browser.",
  },
  {
    icon: Timer,
    title: "Off the instant you decide",
    body: "Set an expiry or revoke any link in one tap. Access ends immediately — old links simply stop working.",
  },
] as const;

/*
 * A calm night sky: slow twinkles kept to the margins, since the glass cards
 * would show a star straight through their copy. Where the margins are depends
 * on the layout, so each star says from which breakpoint it may show: on a
 * phone the content fills the screen and only the two by the eyebrow stay;
 * the one between the lock and the cards needs the two-column desktop layout.
 */
const SHOW = { all: "", sm: "hidden sm:block", lg: "hidden lg:block" } as const;
const STARS = [
  { top: "12%", left: "6%", size: "h-2.5 w-2.5", dur: "3.4s", delay: "0s", show: SHOW.sm },
  { top: "18%", left: "40%", size: "h-2 w-2", dur: "4.2s", delay: "1.1s", show: SHOW.sm },
  { top: "9.5%", left: "64%", size: "h-2.5 w-2.5", dur: "3.8s", delay: "0.6s", show: SHOW.all },
  { top: "15%", left: "93%", size: "h-3 w-3", dur: "4.4s", delay: "1.8s", show: SHOW.all },
  { top: "47%", left: "2.5%", size: "h-2 w-2", dur: "3.1s", delay: "1.6s", show: SHOW.sm },
  { top: "71%", left: "42%", size: "h-2 w-2", dur: "4.6s", delay: "0.3s", show: SHOW.lg },
  { top: "64%", left: "97%", size: "h-2.5 w-2.5", dur: "3.6s", delay: "1.3s", show: SHOW.sm },
  { top: "88%", left: "24%", size: "h-3 w-3", dur: "4s", delay: "0.9s", show: SHOW.sm },
  { top: "92%", left: "57%", size: "h-2 w-2", dur: "3.3s", delay: "2s", show: SHOW.sm },
  { top: "86%", left: "86%", size: "h-2.5 w-2.5", dur: "3.9s", delay: "0.5s", show: SHOW.sm },
] as const;

/*
 * Signature: "Decrypt + hinge flip". The headline decrypts left → right out
 * of sky-blue noise, the sub and the lock follow, then the cards swing open
 * on their left hinge in reading order, each icon popping once its card is
 * in. `<+x` starts a step x seconds after the previous one STARTED.
 */
const TIMELINE: Timeline = [
  fx.fade("[data-fx=eyebrow]", { duration: 0.3 }),
  fx.scramble("[data-fx=title] [data-char]", { duration: 1, at: "-0.15" }),
  fx.fade("[data-fx=sub]", { at: "-0.3" }),
  fx.pop("[data-fx=lock]", { from: 0.6, at: "-0.25" }),
  ...CARDS.flatMap((_, i) => [
    fx.flip(`[data-fx=card-${i}]`, { at: i === 0 ? "<+0.2" : "<+0.12" }),
    fx.pop(`[data-fx=icon-${i}]`, { at: "<+0.22" }),
  ]),
];

export function Security() {
  return (
    <Scene
      id="security"
      label="Privacy"
      tone="dark"
      timeline={TIMELINE}
      className="bg-gradient-to-b from-indigo-950 via-slate-900 to-slate-950 text-slate-100"
    >
      {/* Backdrop: deep night, soft glows, a twinkling star field and fine grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 top-1/4 h-[30rem] w-[30rem] rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -right-32 -top-24 h-[26rem] w-[26rem] rounded-full bg-sky-500/10 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 h-96 w-96 rounded-full bg-violet-500/15 blur-3xl" />
        {STARS.map((s, i) => (
          <Star
            key={i}
            className={cn("absolute animate-pulse text-sky-200/70", s.size, s.show)}
            style={{ top: s.top, left: s.left, animationDuration: s.dur, animationDelay: s.delay }}
          />
        ))}
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-screen invert" />
      </div>

      <div className="mx-auto grid w-full max-w-6xl flex-1 content-center items-center gap-5 px-6 pb-6 pt-20 sm:gap-8 sm:pb-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
        {/* Copy, with the lock glowing beneath it on desktop. */}
        <div>
          <p data-fx="eyebrow" className="text-sm font-semibold uppercase tracking-wide text-sky-300">
            Privacy &amp; safety
          </p>
          <h2
            data-fx="title"
            className="mt-3 font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl"
            style={{ "--fx-glyph-color": "#7dd3fc" } as CSSProperties}
          >
            <SplitChars text="Built to protect your family's information." />
          </h2>
          <p data-fx="sub" className="mt-4 max-w-xl text-pretty text-[15px] text-slate-300 sm:text-lg">
            The share link is the heart of Leena's List — designed so you stay in control of who sees what,
            and for how long.
          </p>

          <div className="relative mt-8 hidden w-52 lg:block xl:w-60">
            <div data-fx="lock" className="relative">
              <div aria-hidden="true" className="absolute inset-[14%] rounded-full bg-sky-400/30 blur-3xl" />
              <div className="relative animate-float">
                <img
                  src={privacyLock}
                  alt=""
                  aria-hidden="true"
                  width={560}
                  height={560}
                  loading="lazy"
                  draggable={false}
                  className="h-auto w-full select-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Four promises, 2×2 from sm up; compact rows on a phone. */}
        <ul role="list" className="grid gap-2 sm:grid-cols-2 sm:gap-4">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <li
                key={card.title}
                data-fx={`card-${i}`}
                className="origin-left rounded-2xl border border-white/10 bg-white/[0.06] p-3 transition-colors hover:bg-white/[0.09] sm:rounded-3xl sm:p-6"
              >
                <div className="flex items-center gap-3 sm:block">
                  <span
                    data-fx={`icon-${i}`}
                    className="inline-flex size-8 shrink-0 items-center justify-center rounded-xl bg-sky-400/10 text-sky-300 ring-1 ring-inset ring-sky-300/20 sm:size-11"
                  >
                    <Icon className="size-4 sm:size-5" aria-hidden="true" />
                  </span>
                  <h3 className="text-[15px] font-semibold text-white sm:mt-4 sm:text-base">{card.title}</h3>
                </div>
                <p className="mt-1.5 text-pretty text-[13px] leading-snug text-slate-300 sm:text-sm sm:leading-relaxed">
                  {card.body}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </Scene>
  );
}
